import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'ghost'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-gold/10 text-gold border border-gold/20 hover:bg-gold/20 hover:border-gold/30 hover:text-gold-bright',
  secondary:
    'bg-cosmic-dusk text-star-soft border border-cosmic-mist hover:bg-cosmic-aurora hover:text-star-bright hover:border-violet-dark',
  ghost:
    'bg-transparent text-star-muted border border-transparent hover:bg-cosmic-dusk/80 hover:text-star',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs rounded-md',
  md: 'px-4 py-2 text-sm rounded-lg',
  lg: 'px-6 py-2.5 text-sm rounded-xl',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-medium',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50',
        'active:scale-[0.98] transition-all duration-150',
        'disabled:pointer-events-none disabled:opacity-40',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
})
